import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { PageHero } from "@/components/page-hero";
import { Section, SectionTitle } from "@/components/section";
import { Reveal } from "@/components/reveal";
import { AnimatedGallery } from "@/components/animated-gallery";
import { CtaBand } from "@/components/cta-band";
import { ResponsiveImg } from "@/components/responsive-img";
import { ARTWORKS } from "@/lib/site-data";
import { lcpPreload, imgSizes } from "@/lib/images";

export const Route = createFileRoute("/art")({
  head: () => ({
    meta: [
      { title: "Art Gallery | Pastels & Oils by Shirley Hunsberger" },
      {
        name: "description",
        content:
          "Original pastel and oil paintings by Shirley Hunsberger of Fuquay-Varina — landscapes, portraits and still life, with commissions available.",
      },
      {
        property: "og:title",
        content: "Art Gallery | Paintings by Shirley Hunsberger",
      },
      {
        property: "og:description",
        content:
          "Pastel and oil landscapes, portraits and still life — commissions welcome, framed in-house.",
      },
    ],
    links: [lcpPreload("art-jordan-lake")],
  }),
  component: ArtPage,
});

function ArtPage() {
  return (
    <>
      <PageHero
        eyebrow="Art gallery"
        title={
          <>
            The framer is also
            <br />
            <span className="amber-text italic">the painter.</span>
          </>
        }
        lead="Shirley works in pastel and oil — landscapes from Jordan Lake to England, portraits, and still life. Commissions are welcome, and can be framed in-house."
        image="art-jordan-lake"
        alt="Pastel landscape of Jordan Lake by Shirley Hunsberger"
      />

      <Section>
        <AnimatedGallery
          items={ARTWORKS.map((a) => ({
            image: a.image,
            caption: a.title,
            meta: a.medium,
          }))}
        />
      </Section>

      <Section tone="mist">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <SectionTitle
            eyebrow="Commissions"
            title="A portrait, a place, a moment."
            lead="Portrait and landscape commissions in pastel or oil, worked up from your photographs and conversations about the piece. Sizes, mediums and timelines are discussed the same way framing is — in person, unhurried."
          />
          <Reveal delay={100}>
            <div className="plate">
              <ResponsiveImg
                name="art-pastel-portrait"
                sizes={imgSizes.half}
                alt="Pastel portrait commission"
                loading="lazy"
                decoding="async"
                className="aspect-[4/3] w-full object-cover"
              />
            </div>
          </Reveal>
        </div>
        <Reveal delay={150} className="mt-12">
          <Link to="/classes" className="btn-primary">
            See art classes <ArrowRight className="size-3.5" />
          </Link>
        </Reveal>
      </Section>

      <CtaBand />
    </>
  );
}
